import { IonIcon } from "@ionic/react";
import { barcodeOutline } from "ionicons/icons";
import React, { useMemo } from "react";
import type { StoredPurchasedTicket } from "../../store/persistence/slices/purchasedItineraries.slice";
import { resolveBarcodeRender } from "./ticketViewer.tools";

/**
 * Props of {@link TicketBarcode}.
 */
export interface TicketBarcodeProps { "ticket": StoredPurchasedTicket }

/**
 * Barcode zone of a ticket card: canonical QR payload or stored image at a
 * scan-friendly size, with graceful placeholders when nothing is displayable.
 *
 * @param props - Component props.
 * @returns The barcode visual.
 */
export const TicketBarcode: React.FC<TicketBarcodeProps> = ( { ticket } ) => {
    const render = useMemo( () => resolveBarcodeRender( ticket ), [ ticket ] );

    if ( render.kind === "qr" || render.kind === "image" ) {
        return (
            <img
                alt="Code-barres du billet"
                className="h-full w-full [image-rendering:pixelated]"
                draggable={false}
                src={render.dataUrl}
            />
        );
    }

    return (
        <div
            className="flex h-full w-full flex-col items-center justify-center gap-2 p-4 text-center text-slate-500"
            data-testid="ticket-barcode-placeholder"
        >
            <IonIcon
                aria-hidden={true}
                className="h-10 w-10"
                icon={barcodeOutline}
            />
            <p className="text-xs font-medium leading-snug">
                {render.kind === "unsupported"
                    ? "Code-barres non affichable."
                    : "Code-barres non disponible."}
            </p>
            {ticket.externalTicketReference !== "" && (
                <p className="text-[10px] leading-snug text-slate-400">
                    Réf.
{" "}
{ticket.externalTicketReference}
                </p>
            )}
        </div>
    );
};
